import { print, type Jast } from '@atlaskit/jql-ast';
import type { SelectedOrderByFields } from '@atlassian/jira-jql-builder-basic-order-by-select/src/types.tsx';
import type { JQLEditorUIWithErrorBoundary$data } from '@atlassian/jira-relay/src/__generated__/JQLEditorUIWithErrorBoundary.graphql';
import { getAstSortFields, type AstSortField } from '../../../utils/get-ast-sort-fields/index.tsx';

/**
 * Compares two lists of sort fields by checking the `jqlTerm` and `direction` but ignoring all other
 * properties such as display name.
 *
 * Does NOT enforce Array / Object equality, only compares the values within. I.e the following
 * will return true, even though they are different arrays:
 *
 * ```
 *  const array1 = [{jqlTerm: 'foo', direction: 'ASC'}]
 *  const array2 = [{jqlTerm: 'foo', direction: 'ASC'}]
 *  compareSortFields(array1, array2) // true
 * ```
 */
export const compareSortFields = (
	sortFields1: SelectedOrderByFields | AstSortField[],
	sortFields2: SelectedOrderByFields | AstSortField[],
): boolean => {
	if (sortFields1.length !== sortFields2.length) {
		return false;
	}

	return sortFields1.every(
		(sortField, index) =>
			sortField.jqlTerm === sortFields2[index].jqlTerm &&
			sortField.direction === sortFields2[index].direction,
	);
};

const getHydrationDisplayNameMap = (
	hydrationData: JQLEditorUIWithErrorBoundary$data,
): Map<string, string> => {
	const hydrationMap = new Map();
	(
		hydrationData?.jira?.jqlBuilder?.hydrateJqlQuery ??
		hydrationData?.jira?.jqlBuilder?.hydrateJqlQueryForFilter
	)?.fields?.map(
		(field) =>
			field.field &&
			field.jqlTerm &&
			hydrationMap.set(field.jqlTerm.toLowerCase(), field.field.displayName ?? ''),
	);
	return hydrationMap;
};

export const getNextSelectedOrderByFields = ({
	ast,
	hydrationData,
	selectedOrderByFields,
}: {
	ast: Jast;
	hydrationData: JQLEditorUIWithErrorBoundary$data;
	selectedOrderByFields: SelectedOrderByFields;
}): SelectedOrderByFields => {
	const astSortFields = getAstSortFields(ast);
	const hydrationDisplayNameMap = getHydrationDisplayNameMap(hydrationData);

	const nextSelectedOrderByFields: SelectedOrderByFields = astSortFields.map((astSortField) => {
		let displayText: string;

		const hydratedDisplayName = hydrationDisplayNameMap.get(astSortField.jqlTerm.toLowerCase());

		const existingField = selectedOrderByFields.find(
			(selectedOrderByField) => selectedOrderByField.jqlTerm === astSortField.jqlTerm,
		);

		if (hydratedDisplayName !== undefined) {
			displayText = hydratedDisplayName;
		} else {
			displayText = existingField?.displayText ?? '';
		}

		const selectedOrderByField = {
			displayText,
			jqlTerm: astSortField.jqlTerm,
			direction: astSortField.direction,
		};

		return selectedOrderByField;
	});

	return nextSelectedOrderByFields;
};

/**
 * Helper for converting a jast back into a JQL string
 */
export const printJast = (jast: Jast): string =>
	print(jast, {
		operatorCase: 'upper',
	});
